import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Container, Package, X, Plus } from "lucide-react";
import { Reveal, MaskLine } from "@/components/site/Reveal";
import PackTicker from "@/components/site/PackTicker";
import CTABand from "@/components/site/CTABand";
import { BISCUITS, CONFECTIONERY, PILE_PACKS } from "@/data/content";

const ProductsPage = () => {
  const [open, setOpen] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [sp, setSp] = useSearchParams();
  const active = BISCUITS[open];

  const openProduct = (id, scroll = true) => {
    const idx = BISCUITS.findIndex((b) => b.id === id);
    if (idx >= 0) setOpen(idx);
    setSp({ p: id }, { replace: true });
    if (scroll) document.getElementById("range-section")?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const pid = sp.get("p");
    if (pid) {
      const idx = BISCUITS.findIndex((b) => b.id === pid);
      if (idx >= 0) setOpen(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main data-testid="page-products">
      {/* HERO */}
      <section className="bg-brand-choc pt-36 pb-24 md:pt-48 md:pb-28 text-brand-cream">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <MaskLine><p className="eyebrow-gold">Products · Made in India</p></MaskLine>
          <h1 className="mt-6 font-display font-light tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-8xl">
            <MaskLine delay={0.1}>Ask us anything.</MaskLine>
            <MaskLine delay={0.3}><span className="italic text-brand-gold">We bake it.</span></MaskLine>
          </h1>
          <MaskLine delay={0.5}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-brand-cream/80">
              Every product below answers the three questions every buyer asks — which flavours,
              which grammages, which packing. Tap a question to open it.
            </p>
          </MaskLine>
        </div>
      </section>

      {/* PACK TICKER */}
      <section className="overflow-hidden border-b border-brand-line bg-brand-sand/60 py-14 md:py-16">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <Reveal><p className="eyebrow">Straight off the line</p></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display font-light text-2xl md:text-4xl tracking-tight">Pick a pack — click for details</h2>
          </Reveal>
        </div>
        <div className="mt-8">
          <PackTicker packs={BISCUITS} onPick={openProduct} />
        </div>
      </section>

      {/* Q&A RANGE */}
      <section id="range-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-20 md:py-28">
        <Reveal><h2 className="font-display font-light text-3xl md:text-5xl tracking-tight">The range, asked &amp; answered</h2></Reveal>
        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-7" data-testid="product-accordion">
            {BISCUITS.map((p, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={p.id}
                  className={`rounded-2xl border transition-colors duration-300 ${isOpen ? "border-brand-red/40 bg-white shadow-sm" : "border-brand-line bg-white/60"}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(i)}
                    data-testid={`faq-toggle-${p.id}`}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-4 p-5 text-left"
                  >
                    <img src={p.img} alt="" className="h-14 w-20 shrink-0 rounded-lg bg-white object-contain" />
                    <span className="flex-1">
                      <span className="block font-display text-lg leading-snug md:text-xl">{p.name}</span>
                      <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted">
                        {p.kind} · {p.flavours.length} flavour{p.flavours.length > 1 ? "s" : ""} · {p.grammages.length} grammages
                      </span>
                    </span>
                    <Plus className={`h-5 w-5 shrink-0 text-brand-red transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-6">
                          <p className="text-sm leading-relaxed text-brand-muted md:text-base">{p.blurb}</p>
                          <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-red">Flavours</p>
                          <div className="mt-2 flex flex-wrap gap-2" data-testid={`faq-flavours-${p.id}`}>
                            {p.flavours.map((f) => (
                              <span key={f} className="rounded-full bg-brand-choc px-4 py-1.5 text-xs font-bold text-brand-cream">{f}</span>
                            ))}
                          </div>
                          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-red">Grammages</p>
                          <div className="mt-2 flex flex-wrap gap-2" data-testid={`faq-grammages-${p.id}`}>
                            {p.grammages.map((g) => (
                              <span key={g} className="rounded-full border border-brand-line px-4 py-1.5 font-mono text-xs text-brand-ink">{g}</span>
                            ))}
                          </div>
                          <p className="mt-4 flex items-center gap-2 text-xs text-brand-muted">
                            <Package className="h-3.5 w-3.5 shrink-0 text-brand-gold" /> {p.packing}
                          </p>
                          <Link to="/contact" data-testid={`faq-enquire-${p.id}`} className="link-underline mt-5 inline-block text-sm font-bold text-brand-red">
                            Enquire about {p.name}
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* ACTIVE IMAGE */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  onClick={() => setZoom(active.img)}
                  data-testid={`qa-image-${active.id}`}
                  className="group relative cursor-zoom-in overflow-hidden rounded-2xl border border-brand-line bg-white"
                >
                  {active.pack ? (
                    <div className="flex h-[400px] items-center justify-center" style={{ backgroundColor: `${active.accent}1a` }}>
                      <img src={active.img} alt={active.name} className="pack-shadow w-full max-w-[430px] px-5 py-8 transition-transform duration-700 group-hover:scale-110" />
                    </div>
                  ) : (
                    <img src={active.img} alt={active.name} className="h-[400px] w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  )}
                  <span className="absolute bottom-3 right-3 rounded-full bg-brand-choc/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-brand-cream">
                    Hover to zoom · click to enlarge
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* CONFECTIONERY */}
      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-20 md:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <img src={CONFECTIONERY.img} alt="Confectionery range" className="aspect-[4/5] w-full cursor-zoom-in rounded-2xl object-cover transition-transform duration-700 hover:scale-[1.02]" onClick={() => setZoom(CONFECTIONERY.img)} data-testid="confectionery-zoom" />
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow">The sweet side · {CONFECTIONERY.unit}</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">All types of confectionery</h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-brand-muted">{CONFECTIONERY.blurb}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="confectionery-items">
                {CONFECTIONERY.items.map((it) => (
                  <span key={it} className="rounded-full bg-brand-choc px-5 py-2.5 text-sm font-bold text-brand-cream">{it}</span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LOGISTICS */}
      <section className="noise-dark bg-brand-choc text-brand-cream">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-20 md:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal><p className="eyebrow-gold">Export logistics</p></Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">Stuffed at our factory. Sealed on your terms.</h2>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 space-y-5 text-base leading-relaxed text-brand-cream/80">
                  <p className="flex gap-4"><Package className="mt-1 h-5 w-5 shrink-0 text-brand-gold" />Multiple grammages and packing formats for every SKU — retail packs, pile-packs, multipacks and cartons built to your market.</p>
                  <p className="flex gap-4"><Container className="mt-1 h-5 w-5 shrink-0 text-brand-gold" />Factory stuffing into 40ft high-cube containers, available for all major global ports.</p>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <img src="images/container_bay.jpeg" alt="Container lorry being stuffed at the factory" className="aspect-[16/10] w-full cursor-zoom-in rounded-2xl object-cover transition-transform duration-700 hover:scale-[1.02]" onClick={() => setZoom("images/container_bay.jpeg")} data-testid="container-zoom" />
            </Reveal>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:max-w-3xl" data-testid="pile-packs">
            {PILE_PACKS.map((pp, i) => (
              <Reveal key={pp.label} delay={i * 0.08}>
                <div className="overflow-hidden rounded-2xl border border-brand-cream/15 bg-brand-cream/5">
                  <button type="button" onClick={() => setZoom(pp.img)} data-testid={`pile-pack-zoom-${i}`} aria-label={`Zoom ${pp.label}`} className="block w-full cursor-zoom-in">
                    <img src={pp.img} alt={pp.label} className="h-56 w-full object-cover transition-transform duration-700 hover:scale-110" />
                  </button>
                  <p className="p-4 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-cream/70">{pp.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />

      {/* LIGHTBOX */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            data-testid="product-lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-choc/90 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoom(null)}
          >
            <motion.img
              src={zoom}
              alt="Zoomed product view"
              data-testid="lightbox-image"
              initial={{ scale: 0.86, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[84vh] max-w-[92vw] rounded-2xl bg-white p-2 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              data-testid="lightbox-close"
              aria-label="Close zoom"
              onClick={() => setZoom(null)}
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-brand-cream text-brand-choc transition-transform hover:scale-110"
            >
              <X className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default ProductsPage;
